import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { baixarDocumento, verDocumento } from "@/lib/docs";
import api, { brl, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { RenovacoesPanel } from "@/components/RenovacoesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { APP_STATUS } from "@/lib/status";
import { AnaliseSolicitacao } from "@/components/AnaliseSolicitacao";
import { Loader2, Search, Eye, UserPlus, MessageSquarePlus, Filter, FileText, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { EnderecoCep, CAMPOS_END } from "@/components/EnderecoCep";

const STATUS_OPTS = Object.keys(APP_STATUS);

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [aba, setAba] = useState("todas");
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState([]);
  const [approveVal, setApproveVal] = useState(0);
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const st = aba === "recusadas" ? "recusada" : statusFilter === "todos" ? "" : statusFilter;
    const { data } = await api.get(`/applications`, { params: { q, status: st, arquivadas: aba === "finalizadas" ? 1 : 0 } });
    setApps(data);
    setLoading(false);
  }, [q, statusFilter, aba]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const [params] = useSearchParams();
  useEffect(() => {
    const id = params.get("abrir");
    if (id) api.get(`/applications/${id}`).then(({ data }) => { setSel(data); setApproveVal(data.valor_aprovado || data.valor_desejado || 0); }).catch(() => toast.error("Solicitação não encontrada ou sem permissão"));
  }, [params]);

  const openApp = async (a) => {
    const { data } = await api.get(`/applications/${a.id}`);
    setSel(data);
    setApproveVal(data.valor_aprovado || data.valor_desejado || 0);
    setDocs([]);
    try {
      const r = await api.get(`/applications/${a.id}/documents`);
      setDocs(r.data);
    } catch { /* ignorado */ }
  };
  const approveValue = async () => {
    await api.patch(`/applications/${sel.id}/approve-value`, { valor_aprovado: Number(approveVal) });
    toast.success("Valor aprovado registrado");
    setSel({ ...sel, valor_aprovado: Number(approveVal) });
  };
  const viewDoc = (d) => verDocumento(d);
  const addNote = async () => {
    if (!note) return;
    const { data } = await api.post(`/applications/${sel.id}/note`, { texto: note });
    setSel({ ...sel, notas: [...(sel.notas || []), data] });
    setNote("");
  };
  const convert = async () => {
    try {
      const { data } = await api.post(`/applications/${sel.id}/convert`);
      toast.success("Cliente criado a partir da solicitação");
      setSel(null); load(); navigate(`/clientes/${data.cliente_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível aprovar"); }
  };
  const [endEdit, setEndEdit] = useState({});
  useEffect(() => { if (sel) setEndEdit({ cep: sel.cep || "", rua: sel.rua || "", numero: sel.numero || "", sem_numero: !!sel.sem_numero, complemento: sel.complemento || "", bairro: sel.bairro || "", cidade: sel.cidade || "", estado: sel.estado || "", manual: true }); }, [sel?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const salvarEndereco = async () => {
    try { const r = await api.patch(`/applications/${sel.id}/endereco`, endEdit); toast.success(r.data.endereco_pendente.length ? "Endereço salvo — ainda faltam campos" : "Endereço completo e conferido"); const { data } = await api.get(`/applications/${sel.id}`); setSel(data); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar endereço"); }
  };
  const _convertOld = null;

  return (
    <div className="space-y-5 fade-up" data-testid="applications-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Central de Solicitações</h1>
        <p className="text-slate-500 text-sm">Solicitações recebidas pelo link público de análise.</p>
      </div>
      <RenovacoesPanel />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <Tabs value={aba} onValueChange={(v) => { setAba(v); setStatusFilter("todos"); }}>
          <TabsList>
            <TabsTrigger value="todas" data-testid="apps-tab-todas">Em andamento</TabsTrigger>
            <TabsTrigger value="finalizadas" data-testid="apps-tab-finalizadas">Finalizadas</TabsTrigger>
            <TabsTrigger value="recusadas" data-testid="apps-tab-recusadas">Recusadas</TabsTrigger>
          </TabsList>
        </Tabs>
        {aba === "recusadas" && apps.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="apps-limpar-recusadas"><Trash2 className="w-4 h-4" /> Excluir todas as recusadas ({apps.length})</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Excluir {apps.length} solicitação(ões) recusada(s)?</AlertDialogTitle>
                <AlertDialogDescription>Os cadastros e documentos enviados por esses solicitantes serão removidos permanentemente. Clientes já criados não são afetados.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={async () => { const { data } = await api.post("/applications/limpar-recusadas"); toast.success(`${data.removidas} removida(s)`); load(); }} className="bg-red-600 hover:bg-red-700" data-testid="apps-limpar-confirm">Excluir</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou CPF..." className="pl-9" data-testid="apps-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter} disabled={aba !== "todas"}>
          <SelectTrigger className="w-56" data-testid="apps-status-filter"><Filter className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos em andamento</SelectItem>
            {STATUS_OPTS.filter((s) => !["finalizada", "finalizado", "convertida", "recusada"].includes(s)).map((s) => <SelectItem key={s} value={s}>{APP_STATUS[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="trt-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Valor</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Cidade</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Recebido</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></td></tr>
              : apps.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-slate-400">Nenhuma solicitação.</td></tr>
              : apps.map((a) => (
                <tr key={a.id} className="border-t hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium">{a.nome}<div className="text-xs text-slate-400 mono">{a.cpf}</div></td>
                  <td className="px-4 py-3 hidden md:table-cell font-semibold">{brl(a.valor_desejado)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">{a.cidade || "-"}</td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-500">{fmtDate(a.created_at)}{a.finalizada_em && <div className="text-[11px] text-emerald-700" data-testid={`app-finalizada-${a.id}`}>finalizada {fmtDate(a.finalizada_em)} ({a.finalizacao_modo === "contrato" ? `contrato ${a.contrato_numero || ""}` : "manual"})</div>}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openApp(a)} data-testid={`app-view-${a.id}`}><Eye className="w-4 h-4" /></Button>
                    {a.status === "recusada" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`app-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Excluir solicitação recusada?</AlertDialogTitle>
                            <AlertDialogDescription>{a.nome_completo || a.nome} — o cadastro e os documentos enviados serão removidos permanentemente.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={async () => { try { await api.delete(`/applications/${a.id}`); toast.success("Solicitação excluída"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } }} className="bg-red-600 hover:bg-red-700" data-testid={`app-delete-confirm-${a.id}`}>Excluir</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto slim-scroll" data-testid="app-detail-dialog">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">{sel.nome} <StatusBadge status={sel.status} /></DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <I l="CPF" v={sel.cpf} /><I l="Nascimento" v={fmtDate(sel.data_nascimento)} />
                  <I l="Telefone" v={sel.telefone} /><I l="Telefone de contato" v={sel.telefone_contato} />
                  <I l="Endereço" v={sel.endereco} /><I l="Cidade" v={sel.cidade} />
                  {sel.endereco_pendente?.length > 0 && <div className="col-span-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="app-endereco-pendente">
                    <b>Endereço incompleto — aguardando regularização.</b> Falta: {sel.endereco_pendente.map((k) => CAMPOS_END[k] || k).join(", ")}. A aprovação fica bloqueada até completar e conferir.
                    <EnderecoCep e={endEdit} onChange={setEndEdit} testid="app" />
                    <Button size="sm" onClick={salvarEndereco} className="bg-onyx text-white" data-testid="app-endereco-salvar">Salvar endereço conferido</Button>
                  </div>}
                  <I l="Perfil" v={sel.tipo_atividade === "motorista" ? "Motorista (Uber/99)" : sel.tipo_atividade === "comerciante" ? "Comerciante" : sel.tipo_atividade === "outros" ? `Outros/CLT — ${sel.subtipo_atividade === "clt" ? "CLT" : `Outro: ${sel.profissao || "-"}`}` : "-"} />
                  <I l="Garantia informada" v={sel.possui_garantia === true ? `Sim — ${sel.garantia_descricao}` : sel.possui_garantia === false ? "Não" : "-"} />
                  {(sel.contatos_familiares || []).map((ct, i) => <I key={i} l={`Contato familiar ${i + 1}`} v={`${ct.nome} · ${ct.parentesco}${ct.vinculo ? ` (${ct.vinculo})` : ""} · ${ct.telefone}`} />)}
                  <I l="CNPJ" v={sel.cnpj} />
                  {sel.nome_comercio && <I l="Comércio" v={sel.nome_comercio} />}
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex items-end gap-3 border">
                  <div className="flex-1"><div className="text-xs text-slate-400">Valor solicitado</div><div className="font-bold text-lg">{brl(sel.valor_desejado)}</div></div>
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 mb-1">Valor aprovado (R$)</div>
                    <Input type="number" value={approveVal} onChange={(e) => setApproveVal(e.target.value)} data-testid="app-approve-input" className="bg-white" />
                  </div>
                  <Button onClick={approveValue} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="app-approve-btn">Aprovar</Button>
                </div>
                {sel.motivo && <div className="bg-slate-50 rounded-lg p-3 text-sm"><span className="text-slate-500">Motivo: </span>{sel.motivo}</div>}

                {docs.length > 0 && (
                  <div>
                    <Label>Documentos enviados ({docs.length})</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {docs.map((d) => (
                        <div key={d.id} className="flex items-center gap-1 rounded-lg border border-slate-200 p-2 text-sm min-w-0" data-testid={`app-doc-${d.id}`}>
                          <FileText className="w-4 h-4 gold-text shrink-0" />
                          <span className="truncate flex-1 min-w-0">{d.tipo}{d.modalidade && <span className={`ml-1 text-[10px] uppercase px-1 rounded ${d.modalidade === "digital" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{d.modalidade === "digital" ? "PDF digital" : "foto"}</span>}</span>
                          <button type="button" onClick={() => viewDoc(d)} className="text-xs text-onyx underline shrink-0" data-testid={`app-doc-ver-${d.id}`}>Visualizar</button>
                          <button type="button" onClick={() => baixarDocumento(d)} className="text-xs text-emerald-700 underline shrink-0" data-testid={`app-doc-baixar-${d.id}`}>Baixar</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <AnaliseSolicitacao tipo="externa" sol={sel} compact onChange={async (ev) => { if (ev === "excluida") { setSel(null); load(); return; } const { data } = await api.get(`/applications/${sel.id}`); setSel(data); load(); }} />

                <div>
                  <Label className="flex items-center gap-1"><MessageSquarePlus className="w-4 h-4" /> Observações internas</Label>
                  <div className="space-y-2 mt-2">
                    {(sel.notas || []).map((n, i) => (
                      <div key={`${n.data || n.created_at || ""}-${i}`} className="bg-amber-50 rounded-lg p-2 text-sm border border-amber-100">
                        {n.texto} <span className="text-xs text-slate-400">— {n.autor}</span>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Adicionar observação..." data-testid="app-note-input" />
                      <Button onClick={addNote} variant="outline" data-testid="app-note-add">Add</Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={convert} className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black gap-2" data-testid="app-convert-btn">
                  <UserPlus className="w-4 h-4" /> {sel.cliente_id ? "Ver cliente" : "Aprovar e cadastrar cliente"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const I = ({ l, v }) => (<div><div className="text-xs text-slate-400">{l}</div><div className="font-medium">{v || "-"}</div></div>);
const Label = ({ children, className = "" }) => <div className={`text-sm font-medium ${className}`}>{children}</div>;
